/**
 * Grid claim honesty — gate narrative / fallback copy on verified SED grid
 * evidence. Prevents unvalidated HIFLD fallbacks or empty provider slots from
 * being stated as factual substation distance or nameplate capacity.
 */
import type { EvidenceSection } from '../types/siteEvidence';
import type { GridEvidence } from '../types/siteEvidence';
import { accordingToDataFrom, resolveEvidenceSourceLabel } from './sourceAttribution';

export function isVerifiedGridSection(
  grid: EvidenceSection<GridEvidence> | undefined | null,
): grid is EvidenceSection<GridEvidence> & { data: GridEvidence } {
  if (!grid || grid.status !== 'verified' || !grid.data) return false;
  if (grid.attemptResult && grid.attemptResult !== 'success') return false;
  const src = (grid.source ?? '').toLowerCase();
  if (src.includes('regional estimate') || src.includes('temporarily unavailable')) return false;
  return true;
}

export function formatGridCapacityLabel(capacity: string | undefined | null): string {
  if (!capacity || capacity === 'Unknown') {
    return 'not verified (utility interconnection study required)';
  }
  return `${capacity} (HIFLD voltage-class proxy — not nameplate ATC)`;
}

/** One-line grid summary safe for degraded skeletons and section fallbacks. */
export function formatVerifiedGridSummary(
  grid: EvidenceSection<GridEvidence> | undefined | null,
): string {
  if (!isVerifiedGridSection(grid)) {
    return 'Grid substation distance and available capacity are not verified for this site. HIFLD data was unavailable or incomplete — a utility interconnection study or service letter is required before stating distance or capacity.';
  }
  const d = grid.data;
  const attr = accordingToDataFrom(resolveEvidenceSourceLabel(grid, 'HIFLD'));
  const parts: string[] = [];
  if (typeof d.substationDistKm === 'number' && Number.isFinite(d.substationDistKm)) {
    const name = d.substationName && d.substationName !== 'Unknown' ? d.substationName : 'nearest HIFLD substation';
    parts.push(`Nearest substation (${name}): ${d.substationDistKm} km (HIFLD point geometry)`);
  }
  if (d.voltage && d.voltage !== 'Unknown') parts.push(`Voltage class: ${d.voltage}`);
  if (d.capacity) parts.push(`Regional capacity proxy: ${formatGridCapacityLabel(d.capacity)}`);
  // Structured utility statement (presence-gated) — labeled by its basis so an
  // indication is never narrated as a commitment. Proxy strings above stay
  // byte-identical when absent.
  if (d.capacityDetail && Number.isFinite(d.capacityDetail.mw)) {
    const basisLabel =
      d.capacityDetail.basis === 'utility-indication'
        ? 'utility indication — not a commitment'
        : d.capacityDetail.basis === 'study'
          ? 'interconnection study'
          : 'executed agreement';
    const cls = d.interconnectionClass ? `, interconnection class ${d.interconnectionClass}` : '';
    parts.push(`Utility-stated available capacity: ${d.capacityDetail.mw} MW (${basisLabel}${cls})`);
  }
  return parts.length > 0
    ? `${attr} ${parts.join('. ')}. Nameplate headroom requires utility confirmation.`
    : `${attr} grid data present but incomplete — site-specific utility study required.`;
}
